import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const lineReveal = (i) => ({
  initial: { y: "112%" },
  animate: { y: "0%" },
  transition: { duration: 1.4, ease: [0.76, 0, 0.24, 1], delay: 0.45 + i * 0.18 },
});

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fgOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const fgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative h-[100svh] overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src="/images/promise-lion.png"
          alt="The TABI lion formed from golden smoke rising through darkness"
          className="h-full w-full object-cover object-[50%_28%]"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
      </motion.div>

      <motion.div
        style={{ opacity: fgOpacity, y: fgY }}
        className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center"
      >
        <motion.p
          data-testid="hero-eyebrow"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 0.25 }}
          className="mb-8 font-mono text-[11px] uppercase tracking-[0.5em] text-gold-light/90"
        >
          A Legacy in Essence
        </motion.p>

        <h1 data-testid="hero-heading" className="font-serif font-light leading-[0.92] text-neutral-50">
          <span className="block overflow-hidden pb-1">
            <motion.span {...lineReveal(0)} className="block text-[20vw] tracking-[0.08em] md:text-[13vw]">
              TABI
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span
              {...lineReveal(1)}
              className="block text-[6.5vw] italic tracking-[0.18em] text-gold-light md:text-[4vw]"
            >
              ESSENCE OF STRENGTH
            </motion.span>
          </span>
        </h1>

        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1.4, delay: 1.3, ease: [0.76, 0, 0.24, 1] }}
          className="gold-rule mt-10 w-40"
        />

        <motion.p
          data-testid="hero-tagline"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 1.5, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-md font-serif text-xl italic text-neutral-300 md:text-2xl"
        >
          Honouring the past, inspiring the future.
        </motion.p>
      </motion.div>

      <motion.div
        data-testid="hero-scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2.1 }}
        className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-3"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.4em] text-neutral-500">Scroll</span>
        <motion.span
          animate={{ scaleY: [1, 0.4, 1], opacity: [0.7, 0.3, 0.7] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          className="block h-12 w-px origin-top bg-gradient-to-b from-gold to-transparent"
        />
      </motion.div>
    </section>
  );
}
